import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface HeroSlide {
  id: string;
  image: string;
  tag: string;
  title: string;
  subtitle: string;
}

export default function HeroCarousel({ slides }: { slides: HeroSlide[] }) {
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const go = (i: number) => setIndex((i + slides.length) % slides.length);

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} 神宫寺奈绪影视作品`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/50 to-primary-950/20"></div>
        </div>
      ))}

      <div className="relative z-10 h-full px-4 md:px-6 flex items-center">
        <div className="w-full max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500 text-white text-xs md:text-sm mb-4 md:mb-5">
            <i className="ri-flashlight-fill"></i>
            {slides[index].tag}
          </span>
          <h2 className="font-heading text-background-50 text-3xl md:text-6xl font-bold leading-tight">
            {slides[index].title}
          </h2>
          <p className="mt-4 md:mt-5 text-background-100/90 text-sm md:text-lg leading-relaxed max-w-2xl line-clamp-2 md:line-clamp-3">
            {slides[index].subtitle}
          </p>
          <div className="mt-7 md:mt-9 flex flex-wrap items-center gap-3">
            <button
              onClick={() => navigate(`/detail/${slides[index].id}`)}
              className="px-5 md:px-7 h-11 md:h-12 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm md:text-base font-medium flex items-center gap-2 whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看作品详情
            </button>
            <button
              onClick={() => navigate('/category')}
              className="px-5 md:px-7 h-11 md:h-12 rounded-md border border-background-100/40 hover:bg-background-50/10 text-background-50 text-sm md:text-base flex items-center gap-2 whitespace-nowrap transition-colors cursor-pointer"
            >
              浏览全部作品
            </button>
          </div>
        </div>
      </div>

      <button
        onClick={() => go(index - 1)}
        aria-label="上一张"
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 rounded-full bg-background-50/20 hover:bg-background-50/35 text-background-50 flex items-center justify-center text-xl backdrop-blur-sm transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line"></i>
      </button>
      <button
        onClick={() => go(index + 1)}
        aria-label="下一张"
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 rounded-full bg-background-50/20 hover:bg-background-50/35 text-background-50 flex items-center justify-center text-xl backdrop-blur-sm transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line"></i>
      </button>

      <div className="absolute bottom-5 md:bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2.5">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            onClick={() => go(i)}
            aria-label={`第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}